/**
 * NOTE on redux: the mobile screen dispatched
 * `store/modules/coupons/categorized.getAllList(...)`, a paginated
 * category-filtered fetch endpoint. That module doesn't exist in the web
 * redux store — the web `coupons` duck only has `getBusinessCoupons()`
 * (fetches the current business's full coupon list, no params). This screen
 * fetches that full list once and filters by category/search client-side,
 * which is the closest faithful behavior given what's actually available.
 * Swap to a real paginated categorized fetch if one is added later.
 */
import React, { useCallback,useEffect, useMemo, useState } from 'react';

import { ChevronDown, ChevronUp } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { CustomerCouponCard, Spinner } from 'components';
import { actions as categoriesActions, duck } from 'coupon-common/src/modules/businessCategories';
import { actions as couponActions } from 'coupon-common/src/modules/coupons';
import { dispatchAsAsync, getNormalizedEntity } from 'coupon-common/src/utils';

import { RouteNames } from '../../../constants';
import CategoryList from './CategoryList';
import styles from './CategorySearch.module.scss';

const categoriesKey = duck.businessCategoriesEntity.key;

const CategorySearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [searchKey, setSearchKey] = useState('');
  const [isExpanded, setIsExpanded] = useState(true);

  const couponsState = useSelector(state => state.coupons);
  const businessCategories = useSelector(state => state.businessCategories);

  const { fetching, fetched, entities, result, error } = couponsState;
  const couponsResult = result?.coupons || {};
  const couponItems = entities?.coupons || {};
  const list = (couponsResult.results || []).map(id => couponItems[id]).filter(Boolean);

  const categoriesNormalized = getNormalizedEntity(businessCategories, categoriesKey, null, {
    id: 'cb.screens.entityCategories',
    defaultMessage: 'business categories',
  });

  useEffect(() => {
    dispatch(categoriesActions.getBusinessCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(couponActions.getBusinessCoupons());
  }, [dispatch]);

  useEffect(() => {
    setCategories(categoriesNormalized.entityList.map(category => ({ ...category, isSelected: false })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesNormalized.entityList]);

  const filteredList = useMemo(() => {
    let templist = [...list];

    if (searchKey.trim()) {
      const lower = searchKey.toLowerCase();
      templist = templist.filter(
        item => item.title?.toLowerCase().includes(lower) || item.description?.toLowerCase().includes(lower),
      );
    }

    const selectedCategoryIds = categories.filter(c => c.isSelected).map(c => c.id);
    if (selectedCategoryIds.length === 0) return templist;

    return templist.filter(coupon => coupon.category && selectedCategoryIds.includes(coupon.category.id));
  }, [list, searchKey, categories]);

  const onSelectCategory = categoryId => {
    setCategories(prev => prev.map(c => (c.id === categoryId ? { ...c, isSelected: !c.isSelected } : c)));
  };

  const onChangeSearchText = useCallback(value => setSearchKey(value.trim()), []);

  const goToDetails = couponId => navigate(`${RouteNames.COUPONS_DETAILS}/${couponId}`);
  const goToBusinessDetails = businessId => navigate(`${RouteNames.BUSINESSES_DETAILS}/${businessId}`);

  const toggleFavouriteHandler = (id, isFavourite) => {
    // Business-side coupon listing has no favourite concept on the customer
    // duck; left as a no-op call point in case a business-side favourite
    // action is added later.
    void id;
    void isFavourite;
  };

  return (
    <div className={styles.page}>
      <h2 className={styles.heading}>Search by category</h2>

      <div className={styles.toggleRow}>
        <button type="button" className={styles.toggleBtn} onClick={() => setIsExpanded(!isExpanded)}>
          Categories {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {isExpanded && (
        <CategoryList onSelectCategory={onSelectCategory} onChangeSearchText={onChangeSearchText} categories={categories} />
      )}

      {error && <p className={styles.stateMessage}>Something went wrong loading your coupons.</p>}

      {!error && fetching && !fetched && (
        <div className={styles.stateMessage}>
          <Spinner show size="md" />
        </div>
      )}

      {!error && fetched && filteredList.length === 0 && <p className={styles.stateMessage}>No coupons found.</p>}

      {!error && filteredList.length > 0 && (
        <div className={styles.grid}>
          {filteredList.map((item, index) => (
            <CustomerCouponCard
              key={item.id}
              first={index === 0}
              data={item}
              onPress={() => goToDetails(item.id)}
              goToBusinessDetails={() => goToBusinessDetails(item.business?.id)}
              toggleFavourite={() => toggleFavouriteHandler(item.id, item.is_save_coupon)}
              isFavourite={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CategorySearch;
